import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/api";

const COPY: Record<string, { title: string; label: string; button: string; help: string }> = {
  hold: { title: "Record dispute and hold final notice", label: "What is the applicant disputing?", button: "Save dispute & hold",
    help: "The final adverse action notice will not be sent until you resume it." },
  resume: { title: "Record outcome and resume", label: "Outcome of the reinvestigation", button: "Save outcome & resume",
    help: "For example: agency reinvestigated, record verified as accurate. The final notice goes out on the next morning run if the 5 business days have passed." },
  note: { title: "Add to dispute log", label: "Details", button: "Save to log", help: "Calls, emails, documents received, or anything else for the FCRA paper trail." },
};

export function DisputeDialog({ open, onClose, onSave }: { open: { r: any; action: string } | null; onClose: () => void; onSave: (body: any) => Promise<void> }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ received_on: today, source: "InfoRetrieval", details: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ received_on: today, source: "InfoRetrieval", details: "" }); }, [open, today]);
  if (!open) return null;
  const c = COPY[open.action];
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave({ action: open.action, ...f }); onClose(); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dispute-dialog">
        <DialogHeader><DialogTitle>{c.title}: {open.r.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium">Date received
              <input type="date" required max={today} className="field-input mt-1 h-10 w-full text-sm" value={f.received_on} onChange={(e) => setF({ ...f, received_on: e.target.value })} data-testid="dispute-date-input" />
            </label>
            <label className="text-sm font-medium">Received by / from
              <select className="field-input mt-1 h-10 w-full text-sm" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="dispute-source-select">
                <option>InfoRetrieval</option><option>Diocese</option><option>Both</option><option>Applicant (phone)</option><option>Applicant (email)</option><option>Applicant (mail)</option><option>Other</option>
              </select>
            </label>
          </div>
          <label className="block text-sm font-medium">{c.label}
            <textarea required rows={4} maxLength={2000} className="field-input mt-1 w-full text-sm" value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} data-testid="dispute-details-input" />
          </label>
          <p className="text-xs text-[#64748B]">{c.help}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary btn-sm" onClick={onClose} data-testid="dispute-cancel-button">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary btn-sm" data-testid="dispute-save-button">{busy ? "Saving\u2026" : c.button}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const KIND: Record<string, { t: string; c: string }> = {
  dispute: { t: "Dispute", c: "text-[#0369A1]" }, outcome: { t: "Outcome", c: "text-[#047857]" }, note: { t: "Note", c: "text-[#475569]" },
};

export function PaperTrail({ r }: { r: any }) {
  const items = [
    ...(r.notices || []).map((n: any) => ({ at: n.at, label: `${n.kind === "pre" ? "Pre-adverse" : "Final adverse"} notice ${n.method === "email" ? `emailed to ${n.to}` : "to be mailed"}`, c: "text-[#92400E]" })),
    ...(r.disputes || []).map((d: any) => ({ at: d.at, label: `${KIND[d.kind]?.t}${d.received_on ? ` (received ${fmtDate(d.received_on)}` : ""}${d.source ? `${d.received_on ? ", " : " ("}${d.source})` : d.received_on ? ")" : ""}: ${d.details}`, by: d.by, c: KIND[d.kind]?.c })),
  ].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  if (!items.length) return null;
  return (
    <details className="mt-2 text-xs" data-testid={`dow-paper-trail-${r.id}`}>
      <summary className="cursor-pointer font-semibold text-[#1B365D]">FCRA paper trail ({items.length})</summary>
      <ol className="mt-1.5 space-y-1 border-l-2 border-[#E2E8F0] pl-3">
        {items.map((i, k) => (
          <li key={k} className={i.c}><span className="font-mono text-[#94A3B8]">{fmtDate(i.at)}</span> {i.label}{i.by ? <span className="text-[#94A3B8]"> by {i.by}</span> : null}</li>
        ))}
      </ol>
    </details>
  );
}
